// COPIED from ptc-core web/src/components/dates/CalendarDateInput.tsx @ a29ddc0 (#355), THE REFERENCE (platform-layer
// ruling; FLEET_ARCHITECTURE.md "Shared UI controls"; fleet filter contract v2.3.1, RULE D8' / C68). Core's own
// lineage: ptc-supervision #240 @ c4dbfe9 plus the onBlur revert from @ 04a2326. No changes from the reference: the
// i18n imports and the ./isoDate.ts split are core's too. Conformance: ./CalendarDateInput.test.tsx (core's cases)
// and scripts/no-native-date-input.test.mjs (RULE D8).
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Button, Input, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CalendarRegular, ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { isIsoDate } from './isoDate';

/**
 * THE date control (fleet filter contract v2.3 §12.6, RULE D8 as amended 2026-10-27).
 *
 * Why not a native `<input type="date">` any more: Chromium's own calendar WRITES the field's value
 * when you move between months (Christian, Team Performance → Custom range, 2026-10-27: the month
 * arrow applied minus one month to the value at once). Where a value change commits, the page then
 * reloads around the open calendar and the day can never be chosen. Nothing in the page can stop the
 * browser doing that, so the control has to be one we own. The rule it keeps:
 *
 *   - the month arrows move the VIEW only. The value is untouched (C68);
 *   - only clicking a day (or typing a complete date) sets the value;
 *   - committing that value is the caller's job (an Apply button, a dialog's Save). This control never commits.
 *
 * It renders INLINE, inside its own wrapper, never in a portal, so an enclosing popover's outside-click
 * check still sees a click on a day as inside (§6.6 I3), and Escape closes only the calendar (stopped
 * here), not the popover around it. The value is always `yyyy-MM-dd`, whatever language is displayed.
 */

export interface CalendarDateInputProps {
  /** `yyyy-MM-dd`, or '' for no date. */
  value: string;
  /** Called with a complete `yyyy-MM-dd` (a day clicked, or a full date typed), or '' when cleared. Never on navigation. */
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Only when no wrapping `<Field>` names the input. */
  'aria-label'?: string;
}


interface MonthView { year: number; month: number } // month 0–11

function iso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function viewOf(value: string, fallback: string): MonthView {
  const src = isIsoDate(value) ? value : fallback;
  return { year: +src.slice(0, 4), month: +src.slice(5, 7) - 1 };
}

function shift(v: MonthView, months: number): MonthView {
  const n = v.year * 12 + v.month + months;
  return { year: Math.floor(n / 12), month: ((n % 12) + 12) % 12 };
}

/** Today as `yyyy-MM-dd` in the reader's own calendar. */
function localToday(): string {
  const d = new Date();
  return iso(d.getFullYear(), d.getMonth(), d.getDate());
}

function locale(): string {
  return i18n.language === 'es' ? 'es' : 'en-US';
}

const useStyles = makeStyles({
  root: { position: 'relative', display: 'inline-flex', flexDirection: 'column' },
  input: { minWidth: '150px' },
  popup: {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 1000,
    marginTop: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    width: '252px',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalXS },
  title: { fontWeight: tokens.fontWeightSemibold, textTransform: 'capitalize' },
  grid: { width: '100%', borderCollapse: 'collapse' },
  weekday: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightRegular, padding: '2px 0' },
  cell: { padding: '1px', textAlign: 'center' },
  day: { minWidth: '32px', width: '32px', height: '32px', padding: 0 },
  today: { border: `${tokens.strokeWidthThick} solid ${tokens.colorBrandStroke1}` },
});

export function CalendarDateInput({ value, onChange, min, max, size = 'medium', disabled, 'aria-label': ariaLabel }: CalendarDateInputProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MonthView>(() => viewOf(value, localToday()));
  // The day that holds keyboard focus inside the grid (roving tabindex).
  const [focusDay, setFocusDay] = useState<string | null>(null);

  // A value set from outside (a day picked, a reset) replaces whatever was typed.
  useEffect(() => { setText(value); }, [value]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openCalendar = () => {
    setView(viewOf(value, localToday()));
    setFocusDay(null);
    setOpen(true);
  };

  const pick = (day: string) => {
    setText(day);
    setOpen(false);
    if (day !== value) onChange(day);
  };

  const inRange = (day: string) => (!min || day >= min) && (!max || day <= max);

  return (
    <div ref={rootRef} className={styles.root}>
      <Input
        className={styles.input}
        size={size}
        disabled={disabled}
        aria-label={ariaLabel}
        value={text}
        placeholder={t('datePicker.placeholder')}
        onChange={(_, d) => {
          setText(d.value);
          if (d.value === '') onChange('');
          else if (isIsoDate(d.value) && d.value !== value) onChange(d.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); openCalendar(); }
        }}
        // Text that is not a complete date never stays on screen once the field is left: the field would
        // show "4/20/2026" while the form still holds the old date, and Save would send the old one silently
        // (found by ptc-statetax's port, #103).
        onBlur={() => { if (text !== value && text !== '' && !isIsoDate(text)) setText(value); }}
        contentAfter={
          <Button
            appearance="transparent"
            size="small"
            icon={<CalendarRegular />}
            disabled={disabled}
            aria-label={t('datePicker.open')}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => (open ? setOpen(false) : openCalendar())}
          />
        }
      />
      {open && (
        <MonthGrid
          view={view}
          onView={setView}
          value={value}
          focusDay={focusDay}
          onFocusDay={setFocusDay}
          onPick={pick}
          inRange={inRange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function MonthGrid({
  view, onView, value, focusDay, onFocusDay, onPick, inRange, onClose,
}: {
  view: MonthView;
  onView: (v: MonthView) => void;
  value: string;
  focusDay: string | null;
  onFocusDay: (day: string) => void;
  onPick: (day: string) => void;
  inRange: (day: string) => boolean;
  onClose: () => void;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const gridRef = useRef<HTMLTableElement>(null);
  const today = localToday();
  const lang = locale();

  const { title, weekdays, weeks, dayLabel } = useMemo(() => {
    const monthFmt = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    const wdFmt = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' });
    const fullFmt = new Intl.DateTimeFormat(lang, { dateStyle: 'full', timeZone: 'UTC' });
    // 2026-10-04 is a Sunday: the week starts on Sunday, as the browser calendar it replaces did.
    const names = Array.from({ length: 7 }, (_, i) => wdFmt.format(new Date(Date.UTC(2026, 9, 4 + i))));
    const first = new Date(Date.UTC(view.year, view.month, 1));
    const daysIn = new Date(Date.UTC(view.year, view.month + 1, 0)).getUTCDate();
    const cells: (string | null)[] = Array.from({ length: first.getUTCDay() }, () => null);
    for (let d = 1; d <= daysIn; d++) cells.push(iso(view.year, view.month, d));
    while (cells.length % 7) cells.push(null);
    const rows: (string | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return {
      title: monthFmt.format(first),
      weekdays: names,
      weeks: rows,
      dayLabel: (day: string) => fullFmt.format(new Date(`${day}T00:00:00Z`)),
    };
  }, [view, lang]);

  const monthDays = weeks.flat().filter((d): d is string => d !== null);
  const tabbable =
    (focusDay && monthDays.includes(focusDay) && focusDay) ||
    (monthDays.includes(value) && value) ||
    (monthDays.includes(today) && today) ||
    monthDays[0];

  // After a keyboard move (possibly into another month) put focus on the new day.
  useEffect(() => {
    if (!focusDay) return;
    gridRef.current?.querySelector<HTMLButtonElement>(`button[data-day="${focusDay}"]`)?.focus();
  }, [focusDay, view]);

  const moveFocus = (from: string, days: number) => {
    const d = new Date(`${from}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    const next = iso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    if (next.slice(0, 7) !== from.slice(0, 7)) onView({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
    onFocusDay(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Close the calendar only: an enclosing filter popover must not read this Escape as "discard".
      e.stopPropagation();
      e.preventDefault();
      onClose();
      return;
    }
    const day = (e.target as HTMLElement).dataset?.day;
    if (!day) return;
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (step !== undefined) { e.preventDefault(); moveFocus(day, step); }
  };

  return (
    <div role="dialog" aria-label={t('datePicker.calendar')} className={styles.popup} onKeyDown={onKeyDown}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronLeftRegular />}
          aria-label={t('datePicker.previousMonth')}
          onClick={() => onView(shift(view, -1))}
        />
        <span className={styles.title} aria-live="polite">{title}</span>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronRightRegular />}
          aria-label={t('datePicker.nextMonth')}
          onClick={() => onView(shift(view, 1))}
        />
      </div>
      <table ref={gridRef} role="grid" aria-label={title} className={styles.grid}>
        <thead>
          <tr>
            {weekdays.map((w) => (
              <th key={w} scope="col" className={styles.weekday}>{w}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, i) => (
            <tr key={i}>
              {week.map((day, j) => (
                <td key={j} className={styles.cell} role={day ? 'gridcell' : undefined}>
                  {day && (
                    <Button
                      data-day={day}
                      className={mergeClasses(styles.day, day === today && styles.today)}
                      size="small"
                      appearance={day === value ? 'primary' : 'subtle'}
                      aria-label={dayLabel(day)}
                      aria-pressed={day === value}
                      aria-current={day === today ? 'date' : undefined}
                      tabIndex={day === tabbable ? 0 : -1}
                      disabled={!inRange(day)}
                      onClick={() => onPick(day)}
                    >
                      {Number(day.slice(8))}
                    </Button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
